import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import "./PreCheckinPage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const EMPTY_FORM = {
  doc_type: "passport",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  place_of_birth: "",
  passport_number: "",
  nationality: "",
  date_of_expiry: "",
  sex: "",
  marital_status: "",
  religion: "",
  occupation: "",
  address: "",
  phone_number: "",
  spouse_name: "",
};

type FormData = typeof EMPTY_FORM;

export default function PreCheckinPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking");

  const [step, setStep] = useState<"loading" | "form" | "done" | "error" | "already">("loading");
  const [info, setInfo] = useState<any>(null);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) { setStep("error"); return; }
    axios.get(`${API}/api/pre-checkin/booking/${encodeURIComponent(bookingId)}`)
      .then(res => {
        const d = res.data;
        setInfo(d);
        if (d.alreadySubmitted) { setStep("already"); return; }
        setStep("form");
      })
      .catch(() => setStep("error"));
  }, [bookingId]);

  const set = (key: keyof FormData, val: string) =>
    setForm(f => ({ ...f, [key]: val }));

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.passport_number.trim()) {
      setError("First name, last name and passport/ID number are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/api/pre-checkin/submit/${encodeURIComponent(bookingId!)}`, form);
      setStep("done");
    } catch {
      setError("Failed to submit. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (step === "loading") return (
    <div className="pc-page"><div className="pc-card pc-loading">Loading your check-in form…</div></div>
  );
  if (step === "error") return (
    <div className="pc-page"><div className="pc-card pc-message pc-error">
      <div className="pc-msg-icon">✕</div>
      <h2>Invalid Link</h2>
      <p>This pre check-in link is invalid. Please contact the hotel directly.</p>
    </div></div>
  );
  if (step === "already") return (
    <div className="pc-page"><div className="pc-card pc-message pc-success">
      <div className="pc-msg-icon pc-icon-ok">✓</div>
      <h2>Already Submitted</h2>
      <p>Welcome, {info?.guestName}! Your ID information is already on file. We look forward to welcoming you!</p>
      <div className="pc-stay-info">
        <span>Check-in: {info?.checkIn}</span>
        <span>Check-out: {info?.checkOut}</span>
        <span>{info?.nights} nights</span>
      </div>
    </div></div>
  );
  if (step === "done") return (
    <div className="pc-page"><div className="pc-card pc-message pc-success">
      <div className="pc-msg-icon pc-icon-ok">✓</div>
      <h2>Pre Check-in Complete!</h2>
      <p>Thank you, {info?.guestName}! Your ID details have been received. We'll have everything ready for your arrival.</p>
      <div className="pc-stay-info">
        <span>Check-in: {info?.checkIn}</span>
        <span>Check-out: {info?.checkOut}</span>
        <span>{info?.nights} nights</span>
      </div>
    </div></div>
  );

  return (
    <div className="pc-page">
      <div className="pc-card">
        {/* Header */}
        <div className="pc-header">
          <div className="pc-hotel-badge">AI² HOTEL</div>
          <h1 className="pc-title">Digital Pre Check-in</h1>
          {info && (
            <p className="pc-welcome">
              Welcome, <strong>{info.guestName}</strong>! Please fill in your ID / passport details to speed up check-in.
            </p>
          )}
          {info && (
            <div className="pc-stay-info">
              <span>Check-in: <strong>{info.checkIn}</strong></span>
              <span>Check-out: <strong>{info.checkOut}</strong></span>
              <span><strong>{info.nights}</strong> nights</span>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="pc-form">
          <div className="pc-section-label">ID / Passport Type</div>
          <div className="pc-row">
            <div className="pc-field">
              <label>Document Type</label>
              <select value={form.doc_type} onChange={e => set("doc_type", e.target.value)}>
                <option value="passport">Passport</option>
                <option value="national_id">National ID</option>
                <option value="driving_license">Driving License</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="pc-field">
              <label>Passport / ID Number *</label>
              <input value={form.passport_number} onChange={e => set("passport_number", e.target.value)} placeholder="Document number" />
            </div>
          </div>

          <div className="pc-section-label" style={{ marginTop: 20 }}>Personal Details</div>
          <div className="pc-row">
            <div className="pc-field">
              <label>First Name *</label>
              <input value={form.first_name} onChange={e => set("first_name", e.target.value)} placeholder="As on your ID" />
            </div>
            <div className="pc-field">
              <label>Last Name *</label>
              <input value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="As on your ID" />
            </div>
          </div>

          <div className="pc-row">
            <div className="pc-field">
              <label>Date of Birth</label>
              <input type="date" value={form.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} />
            </div>
            <div className="pc-field">
              <label>Place of Birth</label>
              <input value={form.place_of_birth} onChange={e => set("place_of_birth", e.target.value)} placeholder="City, Country" />
            </div>
          </div>

          <div className="pc-row">
            <div className="pc-field">
              <label>Nationality</label>
              <input value={form.nationality} onChange={e => set("nationality", e.target.value)} placeholder="e.g. Egyptian" />
            </div>
            <div className="pc-field">
              <label>Date of Expiry</label>
              <input type="date" value={form.date_of_expiry} onChange={e => set("date_of_expiry", e.target.value)} />
            </div>
          </div>

          <div className="pc-row">
            <div className="pc-field">
              <label>Sex</label>
              <select value={form.sex} onChange={e => set("sex", e.target.value)}>
                <option value="">Select…</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="pc-field">
              <label>Marital Status</label>
              <select value={form.marital_status} onChange={e => set("marital_status", e.target.value)}>
                <option value="">Select…</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
          </div>

          <div className="pc-section-label" style={{ marginTop: 20 }}>Contact & Other Info</div>

          <div className="pc-row">
            <div className="pc-field">
              <label>Phone Number</label>
              <input value={form.phone_number} onChange={e => set("phone_number", e.target.value)} placeholder="+1 234 567 8901" />
            </div>
            <div className="pc-field">
              <label>Occupation</label>
              <input value={form.occupation} onChange={e => set("occupation", e.target.value)} placeholder="e.g. Engineer" />
            </div>
          </div>

          <div className="pc-field-full">
            <label>Home Address</label>
            <input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Street, City, Country" />
          </div>

          <div className="pc-row">
            <div className="pc-field">
              <label>Religion</label>
              <input value={form.religion} onChange={e => set("religion", e.target.value)} placeholder="Optional" />
            </div>
            <div className="pc-field">
              <label>Spouse Name</label>
              <input value={form.spouse_name} onChange={e => set("spouse_name", e.target.value)} placeholder="If applicable" />
            </div>
          </div>

          {error && <div className="pc-error-msg">{error}</div>}

          <button className="pc-submit-btn" onClick={submit} disabled={saving}>
            {saving ? "Submitting…" : "Submit Pre Check-in"}
          </button>

          <p className="pc-privacy">Your information is encrypted and only shared with hotel staff.</p>
        </div>
      </div>
    </div>
  );
}
